import { useState, useEffect } from "react";
import { doc, onSnapshot, collection, getDocs, Firestore, QuerySnapshot, DocumentSnapshot } from "firebase/firestore";
import { Match, Player, Round } from "../components/utils/MatchUtils";

export interface UseMatchResult {
  match: Match | null;
  loading: boolean;
  error: Error | null;
}

/**
 * Custom hook to fetch and listen for real-time updates to a match and its subcollections.
 *
 * @param db - Firestore instance
 * @param matchId - ID of the match document
 * @returns Real-time match data, loading state, and error state
 */
export const useMatch = (db: Firestore, matchId: string | undefined): UseMatchResult => {
  const [match, setMatch] = useState<Match | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!matchId) {
      setError(new Error("No match ID provided."));
      setLoading(false);
      return;
    }

    const matchRef = doc(db, "matches", matchId);
    const playersRef = collection(matchRef, "players");
    const roundsRef = collection(matchRef, "rounds");

    let unsubscribeMatch: () => void;
    let unsubscribePlayers: () => void;
    let unsubscribeRounds: () => void;

    const fetchMatchAndSubcollections = async () => {
      try {
        // Set up real-time listener for the match document
        unsubscribeMatch = onSnapshot(
          matchRef,
          (matchSnapshot: DocumentSnapshot) => {
            if (!matchSnapshot.exists()) {
              setError(new Error(`Match with ID ${matchId} not found.`));
              setLoading(false);
              return;
            }

            const matchData = matchSnapshot.data() as Match;

            setMatch((prevMatch) => ({
              ...matchData,
              players: prevMatch?.players || [], // Keep existing players data
              rounds: prevMatch?.rounds || [], // Keep existing rounds data
            }));
            setLoading(false);
          },
          (error) => {
            setError(error);
            setLoading(false);
          }
        );

        // Set up real-time listener for the players subcollection
        unsubscribePlayers = onSnapshot(
          playersRef,
          (playersSnap: QuerySnapshot) => {
            const players: Player[] = playersSnap.docs.map((playerDoc) => ({
              id: playerDoc.id,
              ...(playerDoc.data() as Player),
            }));

            setMatch((prevMatch) => ({
                ...prevMatch!,
                players,
              }));
          },
          (error) => {
            setError(error);
          }
        );

        // Set up real-time listener for the rounds subcollection
        unsubscribeRounds = onSnapshot(
          roundsRef,
          (roundsSnap: QuerySnapshot) => {
            const rounds: Round[] = roundsSnap.docs.map((roundDoc) => ({
              id: roundDoc.id,
              ...(roundDoc.data() as Round),
            }));

            setMatch((prevMatch) => ({
              ...prevMatch!,
              rounds,
            }));
          },
          (error) => {
            setError(error);
          }
        );
      } catch (fetchError) {
        setError(fetchError as Error);
        setLoading(false);
      }
    };

    fetchMatchAndSubcollections();

    // Cleanup listeners on unmount
    return () => {
      if (unsubscribeMatch) unsubscribeMatch();
      if (unsubscribePlayers) unsubscribePlayers();
      if (unsubscribeRounds) unsubscribeRounds();
    };
  }, [db, matchId]);

  return { match, loading, error };
};
